import React from 'react';
import { useFlag } from '../lib/flags';

/**
 * HomeViewSwitch — Services · Tracker · Updates, directly under the search bar.
 *
 * Different members open this app for completely different reasons: a
 * beneficiary wants the services directory, someone building a habit wants
 * their tracker, a volunteer wants to know what is on this weekend. Rather than
 * guess, Home can be any of the three and the member picks which one it opens on.
 *
 * Only shown on Home, only when nothing else is going on (no search, no hub
 * selected), and each segment obeys its own feature flag — so with everything
 * off this renders nothing and Home looks exactly as it did before.
 */

const SEGMENTS = [
  { id: 'services', label: 'Services', flag: null },
  { id: 'tracker', label: 'Tracker', flag: 'tracker' },
  { id: 'updates', label: 'Updates', flag: 'updates_view' },
];

export default function HomeViewSwitch({ value, onChange, defaultView, onMakeDefault }) {
  const switchOn = useFlag('home_switch');
  const trackerOn = useFlag('tracker');
  const updatesOn = useFlag('updates_view');

  const enabled = { tracker: trackerOn, updates_view: updatesOn };
  const visible = SEGMENTS.filter((segment) => !segment.flag || enabled[segment.flag]);

  // A one-segment switch is just a label. Say nothing instead.
  if (!switchOn || visible.length < 2) return null;

  return (
    <div className="px-4 pt-3 desktop-shell">
      <div
        className="inline-flex w-full rounded-full bg-muted p-1"
        role="tablist"
        data-testid="home-view-switch"
      >
        {visible.map((segment) => {
          const active = segment.id === value;
          return (
            <button
              key={segment.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`home-view-${segment.id}`}
              onClick={() => onChange(segment.id)}
              className={`flex-1 h-10 rounded-full text-sm font-semibold transition-colors ${
                active ? 'bg-white text-[#0F4C4A] shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {segment.label}
            </button>
          );
        })}
      </div>

      {value !== defaultView && (
        <button
          type="button"
          onClick={() => onMakeDefault(value)}
          data-testid="home-view-make-default"
          className="mt-2 text-xs font-semibold text-[#0F4C4A] underline"
        >
          Make this my home
        </button>
      )}
    </div>
  );
}
